import { useEffect, useState } from 'react';
import { useCart } from '../context/CartContext';
import { fetchOrderStatus } from '../api';
import { useI18n } from '../i18n';
import type { OrderStatusResponse } from '../types';

const POLL_INTERVAL_MS = 5000;

function isTerminal(s: OrderStatusResponse | null): boolean {
  return s?.status === 'approved' || s?.status === 'rejected';
}

export function OrderWaiting() {
  const { orderUuid, setView, lastOrderStatus, setLastOrderStatus, resetOrder } = useCart();
  const { t } = useI18n();
  // Hydrate from the persisted snapshot so a returning user sees the last
  // known result immediately, even before the first poll completes.
  const [status, setStatus] = useState<OrderStatusResponse | null>(lastOrderStatus);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orderUuid) return;
    if (isTerminal(status)) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function pollOnce() {
      try {
        const result = await fetchOrderStatus(orderUuid!);
        if (cancelled) return;
        setStatus(result);
        setLastOrderStatus(result);
        if (result.status === 'pending') {
          timer = setTimeout(pollOnce, POLL_INTERVAL_MS);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Error');
        // Keep trying — transient network failures (mobile sleep, dropped
        // connection) shouldn't permanently kill the loop.
        timer = setTimeout(pollOnce, POLL_INTERVAL_MS);
      }
    }

    function onVisible() {
      // Mobile browsers throttle/suspend background tabs, so the 5s timer
      // can drift or stop. When the tab becomes visible, force an immediate
      // poll instead of waiting for the next tick.
      if (document.visibilityState !== 'visible') return;
      if (cancelled) return;
      if (isTerminal(status)) return;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      pollOnce();
    }

    pollOnce();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
    // status is intentionally not a dep — we only need its terminal-ness as a
    // start-up gate; the closure inside pollOnce always sees the current value
    // through state updaters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderUuid]);

  function goBackToMenu() {
    resetOrder();
    setView('menu');
  }

  if (!orderUuid) {
    return (
      <div className="hc-waiting">
        <button className="hc-btn hc-btn-primary" onClick={() => setView('menu')}>
          {t('nav.menu')}
        </button>
      </div>
    );
  }

  if (status?.status === 'approved') {
    return (
      <div className="hc-waiting">
        <div className="hc-confirmation-icon">&#10003;</div>
        <h2 className="hc-status-approved">{t('status.approved')}</h2>
        {status.estimated_delivery_minutes && (
          <p className="hc-eta">
            {t('status.approvedEta', { minutes: String(status.estimated_delivery_minutes) })}
          </p>
        )}
        <button className="hc-btn hc-btn-primary" onClick={goBackToMenu}>
          {t('status.newOrder')}
        </button>
      </div>
    );
  }

  if (status?.status === 'rejected') {
    return (
      <div className="hc-waiting">
        <div className="hc-confirmation-icon" style={{ background: '#dc3545' }}>&#10007;</div>
        <h2 className="hc-status-rejected">{t('status.rejected')}</h2>
        {status.rejected_reason && (
          <p>{t('status.rejectedReason', { reason: status.rejected_reason })}</p>
        )}
        <button className="hc-btn hc-btn-primary" onClick={goBackToMenu}>
          {t('nav.menu')}
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="hc-waiting">
        <p className="hc-form-error">{error}</p>
        <div className="hc-spinner" />
        <h2>{t('status.pending')}</h2>
      </div>
    );
  }

  return (
    <div className="hc-waiting">
      <div className="hc-spinner" />
      <h2>{t('status.pending')}</h2>
    </div>
  );
}
